import { useEffect, useMemo, useState } from "react";
import { RefreshCw, ShieldAlert, Activity, Globe2, UserRound } from "lucide-react";
import PageShell from "../../components/layout/PageShell";
import { getPublicGenerationAttempts } from "../../services/monitoringService";

function MetricCard({ icon: Icon, label, value, hint }) {
    return (
        <div className="dash-metric-card">
            <div className="dash-metric-icon">
                <Icon size={20} />
            </div>
            <span>{label}</span>
            <strong>{value || 0}</strong>
            <small>{hint}</small>
        </div>
    );
}

function formatDate(value) {
    if (!value) return "-";

    try {
        return new Date(value).toLocaleString();
    } catch {
        return value;
    }
}

function shortValue(value, max = 28) {
    const text = String(value || "");

    if (text.length <= max) return text;

    return `${text.slice(0, max)}...`;
}

export default function AdminMonitoringPage() {
    const [data, setData] = useState(null);
    const [take, setTake] = useState(100);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const load = async () => {
        try {
            setLoading(true);
            setError("");

            const result = await getPublicGenerationAttempts(take);

            setData(result || null);
        } catch (err) {
            console.error(err);
            setError(
                err?.response?.data?.message ||
                "Failed to load generation monitoring data."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const summary = data?.summary || {};
    const recent = Array.isArray(data?.recent) ? data.recent : [];
    const topReasons = Array.isArray(data?.topReasons) ? data.topReasons : [];
    const topIps = Array.isArray(data?.topIps) ? data.topIps : [];

    const blockedRate = useMemo(() => {
        const total = Number(summary.totalToday || 0);
        const blocked = Number(summary.blockedToday || 0);

        if (!total) return 0;

        return Math.round((blocked * 100) / total);
    }, [summary]);

    return (
        <PageShell title="Generation Monitoring">
            <div className="dash-shell">
                <section className="dash-hero-card">
                    <div>
                        <span className="project-kicker">Public Launch Monitoring</span>
                        <h2>Generation Monitoring</h2>
                        <p>
                            Track public preview attempts, blocked requests, owner/IP quota activity,
                            and bot honeypot signals.
                        </p>
                    </div>

                    <div className="dash-test-actions">
                        <select
                            value={take}
                            onChange={(e) => setTake(Number(e.target.value))}
                        >
                            <option value={50}>Last 50</option>
                            <option value={100}>Last 100</option>
                            <option value={250}>Last 250</option>
                            <option value={500}>Last 500</option>
                        </select>

                        <button
                            type="button"
                            className="project-action-btn secondary"
                            onClick={load}
                            disabled={loading}
                        >
                            <RefreshCw size={16} />
                            {loading ? "Refreshing..." : "Refresh"}
                        </button>
                    </div>
                </section>

                {error && (
                    <div className="dash-alert error">
                        <strong>Attention needed</strong>
                        <p>{error}</p>
                    </div>
                )}

                <section className="dash-metric-grid">
                    <MetricCard
                        icon={Activity}
                        label="Attempts Today"
                        value={summary.totalToday}
                        hint="Allowed + blocked"
                    />

                    <MetricCard
                        icon={ShieldAlert}
                        label="Blocked Today"
                        value={summary.blockedToday}
                        hint={`${blockedRate}% block rate`}
                    />

                    <MetricCard
                        icon={Globe2}
                        label="Unique IPs"
                        value={summary.uniqueIpsToday}
                        hint="Last 24 hours"
                    />

                    <MetricCard
                        icon={UserRound}
                        label="Unique Owners"
                        value={summary.uniqueOwnersToday}
                        hint="Browser owner keys"
                    />
                </section>

                <section className="dash-admin-grid">
                    <section className="dash-panel">
                        <div className="dash-panel-head">
                            <div>
                                <span className="project-kicker">Blocked Reasons</span>
                                <h3>Top Block Reasons</h3>
                                <p>Quota, bot, or abuse signals from the public generator.</p>
                            </div>
                        </div>

                        {topReasons.length === 0 ? (
                            <div className="dashboard-empty refined">
                                No blocked attempts in the last 24 hours.
                            </div>
                        ) : (
                            <div className="dash-signal-list">
                                {topReasons.map((item) => (
                                    <div className="dash-signal-item" key={item.reason || "unknown"}>
                                        <div className="dash-signal-count">
                                            {item.count || 0}
                                        </div>

                                        <div>
                                            <strong>{item.reason || "Unknown"}</strong>
                                            <span>Blocked attempts</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    <section className="dash-panel">
                        <div className="dash-panel-head">
                            <div>
                                <span className="project-kicker">Network Watch</span>
                                <h3>Top IP Activity</h3>
                                <p>Highest public generation activity by IP in the last 24 hours.</p>
                            </div>
                        </div>

                        {topIps.length === 0 ? (
                            <div className="dashboard-empty refined">
                                No IP activity found yet.
                            </div>
                        ) : (
                            <div className="dash-quality-list">
                                {topIps.map((item) => (
                                    <div className="dash-quality-item" key={item.ipAddress || "unknown"}>
                                        <div>
                                            <strong>{item.ipAddress || "Unknown IP"}</strong>
                                            <p>{item.blocked || 0} blocked attempts</p>
                                        </div>

                                        <div className="dash-quality-stats">
                                            <span>{item.count || 0} total</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </section>

                <section className="dash-panel">
                    <div className="dash-panel-head">
                        <div>
                            <span className="project-kicker">Recent Attempts</span>
                            <h3>Public Generation Attempts</h3>
                            <p>Latest preview generation guard decisions.</p>
                        </div>
                    </div>

                    <div className="crm-table-wrap">
                        <table className="crm-table">
                            <thead>
                                <tr>
                                    <th>Time</th>
                                    <th>Status</th>
                                    <th>Reason</th>
                                    <th>IP</th>
                                    <th>Owner</th>
                                    <th>Endpoint</th>
                                    <th>Preview</th>
                                </tr>
                            </thead>

                            <tbody>
                                {recent.length === 0 && (
                                    <tr>
                                        <td colSpan="7">
                                            <div className="dashboard-empty refined">
                                                No public generation attempts found.
                                            </div>
                                        </td>
                                    </tr>
                                )}

                                {recent.map((item) => (
                                    <tr key={item.id}>
                                        <td>{formatDate(item.createdAtUtc)}</td>

                                        <td>
                                            <span
                                                className={`beta-status-pill ${
                                                    item.status === "Blocked"
                                                        ? "failed"
                                                        : "passed"
                                                }`}
                                            >
                                                {item.status || "-"}
                                            </span>
                                        </td>

                                        <td>{item.reason || "-"}</td>
                                        <td title={item.ipAddress}>{shortValue(item.ipAddress, 22)}</td>
                                        <td title={item.ownerKey}>{shortValue(item.ownerKey, 24)}</td>
                                        <td>{item.endpoint || "-"}</td>
                                        <td>{item.previewSessionId ? shortValue(item.previewSessionId, 12) : "-"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
        </PageShell>
    );
}